import type { AdjustmentsDayRow, AdjustmentsOrderRow, AdjustmentsReport, AdjustmentsStaffRow } from '@/types'
import { adjustmentReasonLabel } from '@/lib/discountControls'

/**
 * Discounts & service charge (F-FE-N212) — the arithmetic behind the tab, kept
 * pure so it is tested apart from the page.
 *
 * Discount sources come in a fixed order and keep their colour everywhere on
 * the page (bar, chart, legend, drawer). The two "by hand" sources lead: they
 * are the money staff chose to give away, which is the question an owner
 * opens this tab to answer. Colours are CSS variables validated per theme in
 * index.css.
 */

export type DiscountSourceKey = 'manual_bill' | 'manual_lines' | 'promo' | 'card' | 'loyalty'

export interface DiscountSource {
  key: DiscountSourceKey
  field: 'discount_manual_bill' | 'discount_manual_lines' | 'discount_promo' | 'discount_card_promo' | 'discount_loyalty'
  label: string
  /** For a chip beside one bill. */
  short: string
  colour: string
  byHand: boolean
  help: string
}

export const DISCOUNT_SOURCES: DiscountSource[] = [
  {
    key: 'manual_bill',
    field: 'discount_manual_bill',
    label: 'By hand, on the bill',
    short: 'By hand',
    colour: 'var(--chart-disc-manual-bill)',
    byHand: true,
    help: 'A discount a cashier typed in for the whole bill.',
  },
  {
    key: 'manual_lines',
    field: 'discount_manual_lines',
    label: 'By hand, on items',
    short: 'On items',
    colour: 'var(--chart-disc-manual-items)',
    byHand: true,
    help: 'Discounts a cashier put on single items.',
  },
  {
    key: 'promo',
    field: 'discount_promo',
    label: 'Promo codes',
    short: 'Promo code',
    colour: 'var(--chart-disc-promo)',
    byHand: false,
    help: 'Codes from Marketing, including codes that only cover some items.',
  },
  {
    key: 'card',
    field: 'discount_card_promo',
    label: 'Bank card promos',
    short: 'Card promo',
    colour: 'var(--chart-disc-card)',
    byHand: false,
    help: 'Bank card offers. The bank pays these back; see the Card Promos tab.',
  },
  {
    key: 'loyalty',
    field: 'discount_loyalty',
    label: 'Loyalty rewards',
    short: 'Loyalty',
    colour: 'var(--chart-disc-loyalty)',
    byHand: false,
    help: 'Rewards customers redeemed from their loyalty card.',
  },
]

export const SC_PARTS = {
  configured: { label: 'At your set rate', colour: 'var(--chart-sales)' },
  custom: { label: 'Custom', colour: 'var(--chart-sc-custom)' },
} as const

const has = (n: number) => Math.abs(n) > 0.005

export interface AdjustmentTotals {
  orders: number
  subtotal: number
  serviceCharge: number
  scConfigured: number
  scCustom: number
  scConfiguredOrders: number
  scCustomOrders: number
  scWaivedOrders: number
  discounts: number
  discountedOrders: number
  bySource: Record<DiscountSourceKey, number>
  byHand: number
}

export function adjustmentTotals(days: AdjustmentsDayRow[]): AdjustmentTotals {
  const bySource = { manual_bill: 0, manual_lines: 0, promo: 0, card: 0, loyalty: 0 } as Record<DiscountSourceKey, number>
  const t = {
    orders: 0,
    subtotal: 0,
    serviceCharge: 0,
    scConfigured: 0,
    scCustom: 0,
    scConfiguredOrders: 0,
    scCustomOrders: 0,
    scWaivedOrders: 0,
    discounts: 0,
    discountedOrders: 0,
  }
  for (const d of days) {
    t.orders += d.orders
    t.subtotal += d.subtotal
    t.serviceCharge += d.service_charge
    t.scConfigured += d.sc_configured_amount
    t.scCustom += d.sc_custom_amount
    t.scConfiguredOrders += d.sc_configured_orders
    t.scCustomOrders += d.sc_custom_orders
    t.scWaivedOrders += d.sc_waived_orders
    t.discounts += d.discounts
    t.discountedOrders += d.discounted_orders
    for (const s of DISCOUNT_SOURCES) bySource[s.key] += d[s.field]
  }
  const byHand = DISCOUNT_SOURCES.filter(s => s.byHand).reduce((sum, s) => sum + bySource[s.key], 0)
  return { ...t, bySource, byHand }
}

/** A share as a whole percent, or null when there is nothing to divide by. */
export function percentOf(part: number, whole: number): number | null {
  return whole > 0.005 ? (part / whole) * 100 : null
}

/** "12%" / "0.4%" / "—". Under 1% keeps a decimal so a real figure never reads as 0%. */
export function formatShare(p: number | null): string {
  if (p == null) return '—'
  if (p > 0 && p < 1) return `${p.toFixed(1)}%`
  return `${Math.round(p)}%`
}

/** 0.1 → "10%", 0.075 → "7.5%". */
export function formatRate(rate: number): string {
  const pct = Math.round(rate * 10000) / 100
  return `${Number.isInteger(pct) ? pct : pct.toString()}%`
}

/** One horizontal 100% bar: the non-zero parts, each with its share of the whole. */
export interface BarPart {
  key: string
  label: string
  colour: string
  amount: number
  share: number
}

export function discountBar(t: AdjustmentTotals): BarPart[] {
  return DISCOUNT_SOURCES.filter(s => has(t.bySource[s.key])).map(s => ({
    key: s.key,
    label: s.label,
    colour: s.colour,
    amount: t.bySource[s.key],
    share: (t.bySource[s.key] / t.discounts) * 100,
  }))
}

export function serviceChargeBar(t: AdjustmentTotals): BarPart[] {
  return (['configured', 'custom'] as const)
    .map(k => ({ k, amount: k === 'configured' ? t.scConfigured : t.scCustom }))
    .filter(p => has(p.amount))
    .map(p => ({ key: p.k, label: SC_PARTS[p.k].label, colour: SC_PARTS[p.k].colour, amount: p.amount, share: (p.amount / t.serviceCharge) * 100 }))
}

export type ChartMode = 'discounts' | 'service_charge'

/** Recharts rows: one per business day, every series keyed so the stack never re-colours. */
export function chartRows(days: AdjustmentsDayRow[]) {
  return days.map(d => ({
    date: d.date,
    manual_bill: d.discount_manual_bill,
    manual_lines: d.discount_manual_lines,
    promo: d.discount_promo,
    card: d.discount_card_promo,
    loyalty: d.discount_loyalty,
    discounts: d.discounts,
    configured: d.sc_configured_amount,
    custom: d.sc_custom_amount,
    service_charge: d.service_charge,
    custom_orders: d.sc_custom_orders,
    waived_orders: d.sc_waived_orders,
    orders: d.orders,
    discounted_orders: d.discounted_orders,
  }))
}

/** Does the range hold anything this tab reports on? */
export function hasAnything(t: AdjustmentTotals): boolean {
  return has(t.discounts) || has(t.serviceCharge) || t.scWaivedOrders > 0 || t.scCustomOrders > 0
}

// ── one bill ────────────────────────────────────────────────────────────────

export type OrderFilter = 'all' | 'discounted' | 'custom' | 'waived'

export function matchesFilter(o: AdjustmentsOrderRow, f: OrderFilter): boolean {
  switch (f) {
    case 'discounted':
      return has(o.discounts)
    case 'custom':
      return o.sc_kind === 'custom'
    case 'waived':
      return o.sc_kind === 'waived'
    default:
      return true
  }
}

export function filterCounts(orders: AdjustmentsOrderRow[]): Record<OrderFilter, number> {
  return {
    all: orders.length,
    discounted: orders.filter(o => matchesFilter(o, 'discounted')).length,
    custom: orders.filter(o => matchesFilter(o, 'custom')).length,
    waived: orders.filter(o => matchesFilter(o, 'waived')).length,
  }
}

/** The non-zero discount parts of one bill, in source order. */
export function orderDiscountParts(o: AdjustmentsOrderRow): { source: DiscountSource; amount: number }[] {
  return DISCOUNT_SOURCES.filter(s => has(o[s.field])).map(s => ({ source: s, amount: o[s.field] }))
}

/**
 * What happened to one bill's service charge, in words. `money` formats a
 * rupee figure. A custom charge says how it was set: a rate, or a fixed amount.
 */
export function orderServiceChargeText(o: AdjustmentsOrderRow, money: (n: number) => string): string {
  switch (o.sc_kind) {
    case 'custom':
      if (o.sc_override_amount != null) return `Custom, set to ${money(o.sc_override_amount)}`
      if (o.sc_override_rate != null) return `Custom ${formatRate(o.sc_override_rate)}, ${money(o.service_charge)}`
      return `Custom, ${money(o.service_charge)}`
    case 'waived':
      return 'Service charge waived'
    case 'configured':
      return `At your set rate, ${money(o.service_charge)}`
    case 'credit_noted':
      return 'Refunded in full'
    default:
      return 'No service charge'
  }
}

/** Staff, the person who gave the most away by hand first. */
export function staffByHand(rows: AdjustmentsStaffRow[]): (AdjustmentsStaffRow & { byHand: number })[] {
  return rows
    .map(r => ({ ...r, byHand: r.discount_manual_bill + r.discount_manual_lines }))
    .sort((a, b) => b.byHand - a.byHand || b.discounts - a.discounts || b.sc_custom_set + b.sc_waived_set - (a.sc_custom_set + a.sc_waived_set))
}

// ── reasons and given up (F-RPT-N52) ───────────────────────────────────────

export interface ReasonRow {
  code: string
  label: string
  discounts: number
  /** Share of all by-hand discounts in the period. */
  share: number
  discountedOrders: number
  scGivenUp: number
  scWaivedOrders: number
}

/** The Why list: by-hand discounts by reason, largest first; "" is "No reason recorded". */
export function reasonRows(r: AdjustmentsReport): ReasonRow[] {
  const rows = r.reasons ?? []
  const total = rows.reduce((s, x) => s + x.discounts, 0)
  return rows
    .map(x => ({
      code: x.code,
      label: adjustmentReasonLabel(x.code),
      discounts: x.discounts,
      share: total > 0.005 ? (x.discounts / total) * 100 : 0,
      discountedOrders: x.discounted_orders,
      scGivenUp: x.sc_given_up,
      scWaivedOrders: x.sc_waived_orders,
    }))
    .sort((a, b) => b.discounts - a.discounts || b.scGivenUp - a.scGivenUp)
}

/** Service charge given up and added against the set rate, plus bills that predate the figure. */
export function givenUpTotals(days: AdjustmentsDayRow[]): { givenUp: number; added: number; unknown: number } {
  return days.reduce(
    (t, d) => ({
      givenUp: t.givenUp + (d.sc_given_up ?? 0),
      added: t.added + (d.sc_added ?? 0),
      unknown: t.unknown + (d.sc_unknown_orders ?? 0),
    }),
    { givenUp: 0, added: 0, unknown: 0 },
  )
}
